import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { map, Observable, tap } from 'rxjs';
import { GetSalesMarketplaceItemsRequestDto } from '../../../trading/_domain/dtos/get-sales-marketplace-items-request.dto';
import { BaseRequest } from '../../../shared/models/base-request.model';
import { GetSalesMarketplaceItemsResponseDto } from '../../../trading/_domain/dtos/get-sales-marketplace-items-response.dto';
import { ResolveAccountShippingServicesRequestDto } from '../../../trading/_domain/dtos/resolve-account-shipping-services-request.dto';
import { GetShoppingCartsRequestDto } from '../../../trading/_domain/dtos/get-shopping-carts-request.dto';
import { GetCustomerAccountsRequestDto } from '../../../trading/_domain/dtos/get-customer-accounts-request.dto';
import { GetSalesOrdersRequestDto } from '../../../trading/_domain/dtos/get-sales-orders-request.dto';

const apiUrl = environment.apiUrl;
const trazeApiUrl = environment.trazeApi;

@Injectable({
  providedIn: 'root'
})
export class TradingApiService {

  http = inject(HttpClient);

  private cachedData: any = {};

  getListingRootProductCategories(listings: string[]): Observable<any[]> {

    return this.http.post(`${apiUrl}/inventory/getListingRootProductCategories`, {
      inventoryListingIds: listings,
      paging: {
        pageNumber: 1,
        rows: 50
      },
    }).pipe(
      tap((res: any) => {
        this.setCachedData('productCategories', (res?.data?.productCategories || []));
      }),
      map((res: any) => res?.data?.productCategories || [])
    );
  }

  getProductCategoriesByParentId(parentId: string | null = null): Observable<any[]> {

    return this.http.post(`${apiUrl}/products/getProductCategories`, {
      parentId: parentId,
      orderBy: 'name',
      paging: {
        pageNumber: 1,
        rows: 500
      }
    }).pipe(
      tap((res: any) => {
        this.setCachedData('productCategories', (res?.categories || []));
      }),
      map((res: any) => res?.categories || [])
    );
  }

  getListings(request: any, context?: HttpContext) {
    return this.http.post(`${apiUrl}/inventory/getListings`, request, { context });
  }

  getSalesMarketplaceItems(request: GetSalesMarketplaceItemsRequestDto, context?: HttpContext) {
    return this.http.post<GetSalesMarketplaceItemsResponseDto>(`${apiUrl}/trading/getSalesMarketplaceItems`, request, { context });
  }

  getCustomerAccounts(request: GetCustomerAccountsRequestDto, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/getCustomerAccounts`, request, { context })
  }

  setCachedData(collectionName: string, items: any[], idField: string = 'id') {

    if (!items.length) return;

    let collectionItems = this.cachedData[collectionName] || [];
    const ids = collectionItems.map((o: any) => o[idField]);

    if (ids.length) {
      for (const item of items) {
        if (!ids.includes(item[idField])) {
          collectionItems.push(item);
        }
      }
    }
    else {
      collectionItems = [...items];
    }

    this.cachedData[collectionName] = collectionItems;
  }

  getCachedData(collection: string, values: string[], field: string = 'id') {
    return this.cachedData[collection]?.filter((o: any) => values.includes(o[field])) || [];
  }

  // orders

  getSalesOrders(request: GetSalesOrdersRequestDto, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/getSalesOrders`,
      request, { context }
    );
  }

  resolveAccountShippingServices(request: ResolveAccountShippingServicesRequestDto, context?: HttpContext) {
    return this.http.post<any[]>(`${trazeApiUrl}/trading/resolveAccountShippingServices`, request, { context });
  }

  // CARTS

  checkOutShoppingCarts(request: any, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/checkOutShoppingCarts`, request, { context });
  }

  getShoppingCarts(request: GetShoppingCartsRequestDto, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/getShoppingCarts`,
      request, { context }
    );
  }

  deleteShoppingCarts(request: any, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/deleteShoppingCarts`, request, { context });
  }

  deleteShoppingCartItems(request: any, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/deleteShoppingCartItems`, request, { context });
  }

  saveShoppingCarts(request: any, context?: HttpContext) {
    return this.http.post(`${apiUrl}/trading/saveShoppingCarts`, request, { context });
  }

  getLocations(request: any, context?: HttpContext) {
    return this.http.post(`${apiUrl}/biznet/getLocations`,
      request, { context }
    );
  }
}
